import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { CalendarClock, ArrowRight, Radio, Users } from "lucide-react";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

function CountdownBoxes({ secondsTo, color, compact = false, testid }) {
  const [left, setLeft] = useState(Math.floor(Math.max(0, secondsTo)));
  useEffect(() => setLeft(Math.floor(Math.max(0, secondsTo))), [secondsTo]);
  useEffect(() => {
    const id = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  const d = Math.floor(left / 86400);
  const h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const parts = compact ? [[d, "g"], [h, "h"], [m, "m"]] : [[d, "GIORNI"], [h, "ORE"], [m, "MIN"], [s, "SEC"]];
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      {parts.map(([v, label], i) => (
        <div key={label} className="flex items-center gap-2">
          <div className={`chamfer-sm border border-[#2A2A2A] bg-[#050505] ${compact ? "px-2 py-1" : "px-3 py-2"} text-center min-w-[2.6rem]`}>
            <span className={`font-display ${compact ? "text-lg" : "text-3xl"} tabular-nums`} style={{ color }}>{pad(v)}</span>
            {!compact && <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#4A4D51]">{label}</p>}
          </div>
          {i < parts.length - 1 && <span className="font-display text-lg text-[#2A2A2A]">:</span>}
        </div>
      ))}
    </div>
  );
}

function LiveBadge({ testid }) {
  return (
    <span className="chamfer-sm border border-valkyra/60 bg-valkyra/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-valkyra inline-flex items-center gap-2" data-testid={testid}>
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-valkyra opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-valkyra" />
      </span>
      In onda ora
    </span>
  );
}

const fmtDateIt = (iso) => new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });

export default function EventiPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/game-events").then(({ data }) => setData(data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const events = data?.events || [];
  const next = events.find((e) => !e.is_past);
  const others = events.filter((e) => e !== next && !e.is_past);

  return (
    <main className="pt-32 pb-28" data-testid="eventi-page">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">// SALA BRIEFING</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-4">Eventi</h1>
          <p className="text-steel max-w-xl mb-14">
            I grandi show videoludici dell'anno, guardati insieme: ogni evento ha la sua sala qui sul sito, con diretta e chat della community. Niente link esterni — si guarda da qui.
          </p>
        </Reveal>

        {!data ? (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] h-64 animate-pulse" />
        ) : events.length === 0 ? (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10 text-center" data-testid="eventi-empty">
            <CalendarClock className="w-8 h-8 text-[#2A2A2A] mx-auto mb-4" />
            <p className="font-mono text-xs text-steel">Nessun evento in programma al momento.</p>
          </div>
        ) : (
          <>
            {next && (
              <Reveal delay={0.1}>
                <Link
                  to={`/eventi/${next.slug}`}
                  className="chamfer border bg-[#0C0C0C] p-8 md:p-12 block relative overflow-hidden group transition-colors duration-300 mb-14"
                  style={{ borderColor: `${next.color}55` }}
                  data-testid="eventi-next"
                >
                  <div className="absolute top-0 left-0 w-full h-1" style={{ background: next.color }} />
                  <div className="flex flex-col lg:flex-row lg:items-end gap-8">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        {next.is_live ? (
                          <LiveBadge testid="eventi-next-live" />
                        ) : (
                          <span className="chamfer-sm border border-wardogs/40 bg-wardogs/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-wardogs">Prossimo evento</span>
                        )}
                        {next.estimated && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#4A4D51]">data stimata</span>}
                      </div>
                      <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] mb-4 group-hover:text-wardogs transition-colors duration-300">{next.name}</h2>
                      <p className="font-mono text-xs text-steel uppercase tracking-[0.15em] mb-2">{fmtDateIt(next.date_it)}</p>
                      {next.description && <p className="text-sm text-steel leading-relaxed max-w-2xl">{next.description}</p>}
                    </div>
                    <div className="flex flex-col items-start lg:items-end gap-5">
                      {!next.is_live && <CountdownBoxes secondsTo={next.seconds_to} color={next.color} testid="eventi-next-countdown" />}
                      <span className="chamfer-sm bg-wardogs text-[#050505] px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2">
                        Entra nella sala <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            )}

            {others.length > 0 && (
              <>
                <p className="hud-label mb-5">// IN PROGRAMMA</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="eventi-grid">
                  {others.map((e, i) => (
                    <Reveal key={e.id} delay={i * 0.06}>
                      <Link
                        to={`/eventi/${e.slug}`}
                        className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] hover:border-wardogs/50 p-6 flex flex-col gap-4 h-full transition-colors duration-300 group"
                        data-testid={`eventi-card-${e.slug}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="w-8 h-1 chamfer-sm" style={{ background: e.color }} />
                          {e.is_live && <LiveBadge testid={`eventi-live-${e.slug}`} />}
                        </div>
                        <h3 className="font-display text-2xl uppercase leading-tight group-hover:text-wardogs transition-colors duration-300">{e.name}</h3>
                        <p className="font-mono text-[10px] text-steel uppercase tracking-[0.12em]">{fmtDateIt(e.date_it)}</p>
                        <div className="mt-auto flex items-center justify-between gap-3">
                          {!e.is_live && <CountdownBoxes secondsTo={e.seconds_to} color={e.color} compact testid={`eventi-countdown-${e.slug}`} />}
                          {e.estimated && <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#4A4D51]">data stimata</span>}
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </>
            )}

            <Reveal delay={0.15}>
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 mt-12 flex items-start gap-4" data-testid="eventi-reminder-note">
                <Users className="w-5 h-5 text-wardogs shrink-0 mt-0.5" />
                <p className="text-sm text-steel leading-relaxed">
                  <span className="text-white font-bold">Promemoria automatici su Discord</span> — il bot avvisa la community il giorno prima, un'ora prima e all'inizio di ogni show. Gli eventi annuali si ripresentano da soli ogni anno; quando la data ufficiale cambia, lo Staff la aggiorna con un click.
                </p>
              </div>
            </Reveal>
          </>
        )}
      </div>
    </main>
  );
}

export { LiveBadge };
